import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TaxRequest, TaxRequestItems } from '../_models/tax';
import { Cart, CartPlan } from '../_models/cart';
import { Lead } from '../_models/lead';
import { Vehicle } from '../_models/vehicle';
import { Registration } from '../_models/registration';
import { Opportunity } from '../_models/account';
import { Dealer } from '../_models/dealer';
import { submitTax } from '../_actions/tax.actions';
import { submitVertexTax } from '../_actions/vertex-tax.actions';
import brandInfo from '../../environments/brand.json';
import { RatesResponse } from '../_models/rates';
import { getSurchargeAmountFromRates } from '../_utils/surcharge-lookup.util';

export interface TaxSubmissionOptions {
  cart: Cart;
  lead?: Lead;
  vehicle?: Vehicle;
  registration?: Registration;
  opportunity?: Opportunity;
  dealer?: Dealer;
  useSubtotalForSingleProduct?: boolean;
}

interface DiscountProduct {
  planCode: string;
  discountedPrice: number;
}

interface DiscountInfo {
  data: {
    products: DiscountProduct[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class TaxSubmissionService {
  constructor(private store: Store<{}>) {}

  /**
   * Builds and submits a tax request based on provided options
   * Automatically uses discount info from sessionStorage if available
   * @param options - Configuration for building the tax request
   */
  submitTaxRequest(options: TaxSubmissionOptions): void {
    const taxRequest = this.buildTaxRequest(options);
    this.dispatchTaxRequest(taxRequest);
  }

  /**
   * Builds a TaxRequest object from the provided options
   * Checks sessionStorage for discount info and uses discounted prices
   * @param options - Configuration for building the tax request
   * @returns Complete TaxRequest object
   */
  buildTaxRequest(options: TaxSubmissionOptions): TaxRequest {
    const {
      cart,
      lead,
      vehicle,
      registration,
      opportunity,
      dealer,
      useSubtotalForSingleProduct = false
    } = options;

    const isServiceDrive =
      sessionStorage.getItem('appFlowState') === 'serviceDrive';
    const useDealerAddress =
      brandInfo.useDealerAddressForTaxes && dealer;
    const discountProducts = this.getDiscountProductsFromStorage();

    const taxRequest: TaxRequest = {
      opportunityId: opportunity?.opportunityId || null,
      brandName: brandInfo.brand,
      firstName: 'GuestFirst',
      lastName: 'GuestLast',
      city: this.getCity(
        registration,
        vehicle,
        dealer,
        lead,
        !!isServiceDrive,
        !!useDealerAddress
      ),
      country: this.getCountry(dealer, !!useDealerAddress),
      email: 'noreply@sgintl.com',
      phoneNumber: null,
      postalCode: this.getPostalCode(
        registration,
        dealer,
        !!isServiceDrive,
        !!useDealerAddress
      ),
      state: this.getState(
        registration,
        vehicle,
        dealer,
        !!isServiceDrive,
        !!useDealerAddress
      ),
      street1: this.getStreet1(
        registration,
        dealer,
        !!isServiceDrive,
        !!useDealerAddress
      ),
      vin: this.getVin(lead, vehicle, !!isServiceDrive),
      downPayment: cart.downPayment,
      installments: cart.numberOfMonthlyPayments,
      items: this.buildTaxItems(
        cart,
        discountProducts,
        useSubtotalForSingleProduct
      )
    };

    return taxRequest;
  }

  /**
   * Retrieves discount products from sessionStorage if available
   * @returns Array of discount products with planCode and discountedPrice
   */
  private getDiscountProductsFromStorage(): DiscountProduct[] | undefined {
    try {
      const discountInfoStr = sessionStorage.getItem('discountInfo');
      if (discountInfoStr) {
        const discountInfo: DiscountInfo = JSON.parse(discountInfoStr);
        if (discountInfo?.data?.products) {
          return discountInfo.data.products.map(product => ({
            planCode: product.planCode,
            discountedPrice: product.discountedPrice
          }));
        }
      }
    } catch (error) {
      console.warn('Error parsing discountInfo from sessionStorage:', error);
    }
    return undefined;
  }

  /**
   * Dispatches the appropriate tax action based on brand configuration
   * @param taxRequest - The tax request to submit
   */
  private dispatchTaxRequest(taxRequest: TaxRequest): void {
    if (brandInfo.taxService === 'Vertex') {
      this.store.dispatch(submitVertexTax(taxRequest));
    } else {
      this.store.dispatch(submitTax(taxRequest));
    }
  }

  private getFirstName(lead?: Lead): string {
    if (lead?.customer?.firstName) {
      return lead.customer.firstName;
    }
    return 'GuestFirst';
  }

  private getLastName(lead?: Lead): string {
    if (lead?.customer?.lastName) {
      return lead.customer.lastName;
    }
    return 'GuestLast';
  }

  private getCity(
    registration?: Registration,
    vehicle?: Vehicle,
    dealer?: Dealer,
    lead?: Lead,
    isServiceDrive: boolean = false,
    useDealerAddress: boolean = false
  ): string {
    if (useDealerAddress && dealer) {
      return dealer.data.address.city;
    }
    if (isServiceDrive && dealer) {
      return dealer.data.address.city;
    }
    if (registration?.city) {
      return registration.city;
    }
    if (lead?.customer?.city) {
      return lead.customer.city;
    }
    return '';
  }

  private getCountry(
    dealer?: Dealer,
    useDealerAddress: boolean = false
  ): string {
    if (useDealerAddress && dealer) {
      return dealer.data.address.country;
    }
    return brandInfo.country || 'USA';
  }

  private getEmail(lead?: Lead): string {
    if (lead?.customer?.emailId) {
      return lead.customer.emailId;
    }
    return 'noreply@sgintl.com';
  }

  private getPhoneNumber(lead?: Lead): string {
    if (lead?.customer?.phoneNumber1) {
      return lead.customer.phoneNumber1;
    }
    if (lead?.customer?.phoneNumber2) {
      return lead.customer.phoneNumber2;
    }
    return null;
  }

  private getPostalCode(
    registration?: Registration,
    dealer?: Dealer,
    isServiceDrive: boolean = false,
    useDealerAddress: boolean = false
  ): string {
    if (useDealerAddress && dealer) {
      return dealer.data.address.postalCode;
    }
    if (isServiceDrive && dealer) {
      return dealer.data.address.postalCode;
    }
    if (registration?.zip) {
      return registration.zip;
    }
    return '';
  }

  private getState(
    registration?: Registration,
    vehicle?: Vehicle,
    dealer?: Dealer,
    isServiceDrive: boolean = false,
    useDealerAddress: boolean = false
  ): string {
    if (useDealerAddress && dealer) {
      return dealer.data.address.state;
    }
    if (isServiceDrive && dealer) {
      return dealer.data.address.state;
    }
    if (registration?.state) {
      return registration.state;
    }
    if (vehicle?.registeredState) {
      return vehicle.registeredState;
    }
    return '';
  }

  private getStreet1(
    registration?: Registration,
    dealer?: Dealer,
    isServiceDrive: boolean = false,
    useDealerAddress: boolean = false
  ): string {
    if (useDealerAddress && dealer) {
      return dealer.data.address.street1;
    }
    if (isServiceDrive && dealer) {
      return dealer.data.address.street1;
    }
    if (registration?.address) {
      return registration.address;
    }
    return '';
  }

  private getVin(
    lead?: Lead,
    vehicle?: Vehicle,
    isServiceDrive: boolean = false
  ): string {
    if (isServiceDrive && vehicle) {
      return vehicle.vin;
    }
    if (lead?.vehicle?.vin) {
      return lead.vehicle.vin;
    }
    if (vehicle?.vin) {
      return vehicle.vin;
    }
    return '';
  }

  private buildTaxItems(
    cart: Cart,
    discountProducts?: DiscountProduct[],
    useSubtotalForSingleProduct?: boolean
  ): TaxRequestItems[] {
    const items: TaxRequestItems[] = [];
    const shouldUseSubtotalForSingleProduct =
      !!useSubtotalForSingleProduct && cart.plans.length === 1;

    if (shouldUseSubtotalForSingleProduct) {
      const plan = cart.plans[0];
      if (plan) {
        items.push(this.createTaxItem(plan, Number(cart.subtotal)));
      }
      return items;
    }

    /**
     * Opportunity to get rid of the if/else statements. 
     * All brands single or multi product can loop thru their products, check for a discount, and add an item to the task reuqest.
     * 
     * Remember to add surcharges to the unit price AFTER the discount is applied
     * Lines 308-317 look fine. Loop thru the plans, get the discounted price if it exists and create the tax item.
     * Last thing to do is to add the surcharges at the end. Not all brands have surcharges + surcharges are only added to VSC products.
     */
    if (brandInfo.isMultiProduct) {
      const vscSurchargeTotal = this.getVehicleServiceSurchargeTotal(cart);
      for (let i = 0; i < cart.plans.length; i++) {
        const plan = cart.plans[i];
        const discountedProduct = discountProducts?.find(
          (dp) => dp.planCode === plan.planCode
        );

        const baseUnitPrice = Number(
          discountedProduct ? discountedProduct.discountedPrice : plan.planPrice
        );
        const addSurcharge = plan.productCategory === 'Vehicle Service';
        const unitPrice = addSurcharge
          ? baseUnitPrice + vscSurchargeTotal
          : baseUnitPrice;

        items.push(this.createTaxItem(plan, unitPrice));
      }
    } else {
      const plan = cart.plans[0];
      if (plan) {
        const discountedProduct = discountProducts?.find(
          (dp) => dp.planCode === plan.planCode
        );
        const unitPrice = Number(
          discountedProduct ? discountedProduct.discountedPrice : plan.planPrice
        );
        items.push(this.createTaxItem(plan, unitPrice));
      }
    }

    return items;
  }

  private createTaxItem(
    plan: CartPlan,
    unitPrice: number
  ): TaxRequestItems {
    return {
      productName: plan.ratesProductName,
      productSKU: plan.productSkuId,
      quantity: 1,
      unitPrice: unitPrice,
      productSfId: plan.productSkuId,
      productCode: plan.productCode,
      contractNumber: plan.ratesProductName
    };
  }

  private getVehicleServiceSurchargeTotal(cart: Cart): number {
    const rates: RatesResponse = JSON.parse(sessionStorage.getItem('rates'));
    let surchargeTotal = 0;

    if (cart.commercialUse) {
      const professionalServicesAmount = getSurchargeAmountFromRates(
        rates,
        'Professional Services'
      );
      const commercialAmount = getSurchargeAmountFromRates(rates, 'Commercial');
      const lightDutyCommercialAmount = getSurchargeAmountFromRates(
        rates,
        'LIGHT DUTY COMMERCIAL'
      );
      surchargeTotal += professionalServicesAmount;
      surchargeTotal += commercialAmount;
      surchargeTotal += lightDutyCommercialAmount;
    }

    if (cart.liftKit) {
      const liftKitAmount = getSurchargeAmountFromRates(rates, 'Lift Kit');
      surchargeTotal += liftKitAmount;
    }

    if (cart.greyMarket) {
      const greyMarketAmount = getSurchargeAmountFromRates(
        rates,
        'Grey Market'
      );
      surchargeTotal += greyMarketAmount;
    }

    if (cart.snowPlow) {
      const plowAmount = getSurchargeAmountFromRates(rates, 'Plow');
      surchargeTotal += plowAmount;
    }

    if (cart.valet) {
      const valetServiceAmount = getSurchargeAmountFromRates(
        rates,
        'VALET SERVICE'
      );
      surchargeTotal += valetServiceAmount;
    }

    return surchargeTotal;
  }
}

